import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API_BASE_URL from "../config";

const getDefaultRoute = (activeRole, roles) => {
  if (activeRole === "ROLE_ADMIN" || roles.includes("ROLE_ADMIN")) return "/admindashboard";
  if (activeRole === "ROLE_MANAGER" || roles.includes("ROLE_MANAGER")) return "/managerdashboard";
  return "/empdashboard";
};

export default function PagePermissionRoute({ children, pageKey, deniedMessage }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [allowed, setAllowed] = useState(false);
  const [checking, setChecking] = useState(true);
  const hasRedirected = useRef(false);

  useEffect(() => {
    let mounted = true;

    const verifyAccess = async () => {
      const token = localStorage.getItem("token");
      const employeeId = localStorage.getItem("employeeId");
      const activeRole = localStorage.getItem("activeRole") || "";
      const roles = JSON.parse(localStorage.getItem("roles") || "[]");
      const fallbackRoute = getDefaultRoute(activeRole, roles);

      if (!token) {
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          navigate("/", { replace: true });
        }
        return;
      }

      if (roles.includes("ROLE_ADMIN")) {
        if (mounted) {
          setAllowed(true);
          setChecking(false);
        }
        return;
      }

      if (!employeeId) {
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          toast.error("Unable to verify your permissions.");
          navigate(fallbackRoute, { replace: true });
        }
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/permissions/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Permission lookup failed");

        const data = await res.json();
        const hasPagePermission = Boolean(data?.pages?.[pageKey]);

        if (!mounted) return;

        if (hasPagePermission) {
          setAllowed(true);
          setChecking(false);
          return;
        }

        if (!hasRedirected.current) {
          hasRedirected.current = true;
          toast.error(deniedMessage || "You do not have access to this page.");
          navigate(fallbackRoute, {
            replace: true,
            state: { from: location.pathname },
          });
        }
      } catch (error) {
        if (!mounted) return;

        if (!hasRedirected.current) {
          hasRedirected.current = true;
          toast.error("Unable to verify page access.");
          navigate(fallbackRoute, {
            replace: true,
            state: { from: location.pathname },
          });
        }
      } finally {
        if (mounted) setChecking(false);
      }
    };

    verifyAccess();

    return () => {
      mounted = false;
    };
  }, [deniedMessage, location.pathname, navigate, pageKey]);

  if (checking) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f4f6fb" }}>
        <div style={{ fontSize: 14, color: "#64748b" }}>Checking page access...</div>
      </div>
    );
  }

  return allowed ? children : null;
}
